import { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { RefreshCw } from "lucide-react";
import {
  getActivity,
  getSessionEvents,
  listVisibleUsers,
} from "../../components/PerformanceTracker/performanceTrackerApi";
import LoadingOverlay from "../../components/LoadingOverlay";
import ActivityByUserDay from "./ActivityByUserDay";
import ActivityOverviewChart from "./ActivityOverviewChart";
import OverallKpiCards from "./OverallKpiCards";
import IndividualKpiCards from "./IndividualKpiCards";
import IndividualProfileCard from "./IndividualProfileCard";
import PauseBreakdown from "./PauseBreakdown";
import PeopleActivity from "./PeopleActivity";
import ScopeSummary from "./ScopeSummary";
import SessionActivityChart from "./SessionActivityChart";
import SessionDetailDrawer from "./SessionDetailDrawer";
import SessionHistoryTable from "./SessionHistoryTable";
import TrackerStates from "./TrackerStates";
import UserScopeFilter from "./UserScopeFilter";
import {
  activeSecondsByUserDay,
  buildPeopleRows,
  liveSession,
  rangeIncludesToday,
  trackerStateCounts,
} from "./activityScope";
import {
  DATE_PRESETS,
  formatRangeLabel,
  rangeForPreset,
} from "./dateRange";
import "./PerformanceActivityPage.css";

const EMPTY = {
  kpis: {
    activeSeconds: 0,
    sessionCount: 0,
    avgSessionSeconds: 0,
    pauseSeconds: 0,
    manualPauseSeconds: 0,
    autoPauseSeconds: 0,
    priorAvgSessionSeconds: 0,
    priorActiveSeconds: 0,
    priorSessionCount: 0,
    priorPauseSeconds: 0,
  },
  days: [],
  sessions: [],
};

export default function PerformanceActivityPage({
  title = "User Activity",
  embedMode = false,
}) {
  const { instance, accounts } = useMsal();
  const initial = rangeForPreset("today");
  const [preset, setPreset] = useState("today");
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [scopeUser, setScopeUser] = useState(null);

  const searchUsers = useCallback(
    async (q) => listVisibleUsers(instance, accounts, { q }),
    [accounts, instance],
  );

  const load = useCallback(async () => {
    if (!from || !to || from > to) return;
    setLoading(true);
    setError("");
    try {
      const payload = await getActivity(instance, accounts, {
        from,
        to,
        userId: scopeUser?.userId,
      });
      setData(payload || EMPTY);
    } catch (err) {
      setData(EMPTY);
      setError(err?.message || "Unable to load activity");
    } finally {
      setLoading(false);
    }
  }, [accounts, from, instance, scopeUser?.userId, to]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreset = (id) => {
    setPreset(id);
    if (id === "custom") return;
    const next = rangeForPreset(id);
    setFrom(next.from);
    setTo(next.to);
  };

  const openSession = async (session) => {
    setSelected(session);
    setEvents([]);
    setEventsLoading(true);
    try {
      const payload = await getSessionEvents(instance, accounts, session.sessionId);
      setEvents(payload?.events || []);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const selectPerson = (person) => {
    if (!person?.userId) return;
    setScopeUser({
      userId: person.userId,
      userName: person.userName || person.userId,
    });
  };

  const kpis = data.kpis || EMPTY.kpis;
  const days = data.days || EMPTY.days;
  const sessions = data.sessions || EMPTY.sessions;
  const signedInUsers = data.scope?.signedInUsers;
  const singleDay = from === to;
  const dayCount = Math.max(1, days.length);
  const avgPerDay = (kpis.sessionCount / dayCount).toFixed(1).replace(/\.0$/, "");
  const isIndividual = Boolean(scopeUser?.userId);
  const showLiveStates = rangeIncludesToday(from, to);
  const usersWithActivity = data.scope?.usersWithActivity
    ?? new Set(sessions.map((session) => session.userId).filter(Boolean)).size;
  const usersInScope = data.scope?.usersInScope ?? usersWithActivity;
  const peopleRows = useMemo(
    () => buildPeopleRows({
      signedInUsers,
      sessions: data.sessions,
    }),
    [data.sessions, signedInUsers],
  );
  const stateCounts = useMemo(() => trackerStateCounts(peopleRows), [peopleRows]);
  const userDayRows = useMemo(
    () => activeSecondsByUserDay({
      people: peopleRows,
      sessions: data.sessions,
      dates: (data.days || []).map((day) => day.date),
    }),
    [data.days, data.sessions, peopleRows],
  );

  const periodLabel = useMemo(() => {
    if (preset === "today") return "Today";
    if (preset === "yesterday") return "Yesterday";
    if (preset === "this_week") return "This week";
    if (preset === "last_week") return "Last week";
    if (preset === "this_month") return "This month";
    return formatRangeLabel(from, to);
  }, [from, preset, to]);

  const coveragePhrase = preset === "today"
    ? "today"
    : preset === "yesterday"
      ? "yesterday"
      : "during the selected period";

  const scopeContext = isIndividual
    ? `Individual activity · ${scopeUser.userName || scopeUser.userId}`
    : "Overall · currently signed-in users";

  return (
    <div className={`pa-page${embedMode ? " pa-page--embed" : ""}`}>
      <div className="pa-header">
        <div>
          <h1 className="pa-title">{title}</h1>
          <p className="pa-subtitle">{scopeContext}</p>
        </div>
        <div className="pa-header__filters">
          <UserScopeFilter
            value={scopeUser}
            onChange={setScopeUser}
            onSearch={searchUsers}
          />
          <div className="pa-toolbar">
            <div className="pa-preset-group" role="group" aria-label="Date range">
              {DATE_PRESETS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`pa-pill${preset === item.id ? " pa-pill--active" : ""}`}
                  onClick={() => applyPreset(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="pa-refresh"
              aria-label="Refresh activity"
              title="Refresh"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? "pa-refresh__icon pa-refresh__icon--spin" : "pa-refresh__icon"} />
            </button>
          </div>
          {preset === "custom" ? (
            <div className="pa-custom">
              <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
              <span>to</span>
              <input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} />
            </div>
          ) : null}
        </div>
      </div>

      {error ? <p className="pa-error">{error}</p> : null}

      <div className={`pa-body${loading ? " pa-body--loading" : ""}`}>
        <LoadingOverlay show={loading} label="Loading activity..." />
        <div className="pa-body__content">
          {!isIndividual ? (
            <div className={`pa-overview${showLiveStates ? "" : " pa-overview--no-states"}`}>
              <ScopeSummary
                usersInScope={usersInScope}
                usersWithActivity={usersWithActivity}
                periodPhrase={coveragePhrase}
              />
              {showLiveStates ? <TrackerStates counts={stateCounts} /> : null}
              <OverallKpiCards kpis={kpis} preset={preset} />
            </div>
          ) : (
            <>
              <IndividualProfileCard
                userName={scopeUser.userName}
                userId={scopeUser.userId}
                status={liveSession(sessions)?.status || null}
              />
              <IndividualKpiCards
                kpis={kpis}
                preset={preset}
                periodLabel={periodLabel}
                avgPerDay={avgPerDay}
              />
            </>
          )}

          <section className="pa-card pa-section">
            <div className="pa-section__head">
              <div>
                <h2 className="pa-section__title">
                  {isIndividual ? "Activity trend" : "Aggregate activity trend"}
                </h2>
                <p className="pa-section__copy">
                  {singleDay
                    ? isIndividual
                      ? "Active time, manual pauses, and auto-pauses by hour"
                      : "Combined signed-in activity by hour"
                    : isIndividual
                      ? "Active time, manual pauses, and auto-pauses by day"
                      : "Combined signed-in activity by day"}
                </p>
              </div>
              <span className="pa-range-chip">{formatRangeLabel(from, to)}</span>
            </div>
            <ActivityOverviewChart days={days} sessions={sessions} from={from} to={to} />
          </section>

          {isIndividual ? (
            <section className="pa-card pa-section">
              <div className="pa-section__head">
                <div>
                  <h2 className="pa-section__title">Pause breakdown</h2>
                  <p className="pa-section__copy">Manual pause versus auto-pause in this period</p>
                </div>
              </div>
              <PauseBreakdown
                manualPauseSeconds={kpis.manualPauseSeconds}
                autoPauseSeconds={kpis.autoPauseSeconds}
              />
            </section>
          ) : (
            <section className="pa-card pa-section">
              <div className="pa-section__head">
                <div>
                  <h2 className="pa-section__title">Users activity</h2>
                  <p className="pa-section__copy">
                    Signed-in users ranked by active time in the selected period
                  </p>
                </div>
              </div>
              <PeopleActivity people={peopleRows} onSelectUser={selectPerson} />
            </section>
          )}

          {!isIndividual ? (
            <section className="pa-card pa-section">
              <div className="pa-section__head">
                <div>
                  <h2 className="pa-section__title">Pause breakdown</h2>
                  <p className="pa-section__copy">Manual pause versus auto-pause across signed-in users</p>
                </div>
              </div>
              <PauseBreakdown
                manualPauseSeconds={kpis.manualPauseSeconds}
                autoPauseSeconds={kpis.autoPauseSeconds}
              />
            </section>
          ) : null}

          {!isIndividual && !singleDay ? (
            <section className="pa-card pa-section">
              <div className="pa-section__head">
                <div>
                  <h2 className="pa-section__title">Activity by user / day</h2>
                  <p className="pa-section__copy">Active time per signed-in user for each day in the range</p>
                </div>
                <span className="pa-range-chip">{formatRangeLabel(from, to)}</span>
              </div>
              <ActivityByUserDay
                dates={days.map((day) => day.date)}
                rows={userDayRows}
                onSelectUser={selectPerson}
              />
            </section>
          ) : null}

          {isIndividual ? (
            <section className="pa-card pa-section">
              <div className="pa-section__head">
                <div>
                  <h2 className="pa-section__title">Session activity</h2>
                  <p className="pa-section__copy">
                    Active and pause intervals within each session for the selected period
                  </p>
                </div>
              </div>
              <SessionActivityChart sessions={[...sessions].slice().reverse()} />
            </section>
          ) : null}

          {isIndividual ? (
            <section className="pa-card pa-section">
              <SessionHistoryTable
                sessions={sessions}
                showUserColumn={false}
                onView={openSession}
              />
            </section>
          ) : null}
        </div>
      </div>

      {selected ? (
        <SessionDetailDrawer
          session={selected}
          events={events}
          loading={eventsLoading}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}
